/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Car, User, Mail, Phone, CreditCard, CheckCircle, AlertCircle, MapPin } from 'lucide-react';
import '../styles/raffle.css';

const locations = [
  { id: 1, site: 'Bilston', address: 'Morrisons, Black Country Route, Bilston, WV14 0DZ', slug: 'bilston' },
  { id: 2, site: 'Coventry', address: 'Morrisons, Skipworth Road, Binley, Coventry, CV3 2AN', slug: 'coventry' },
  { id: 3, site: 'Derby', address: 'Morrisons, Wheatcroft Road, Derby, DE21 4RY', slug: 'derby' },
  { id: 4, site: 'Enfield', address: 'Morrisons, Southbury Road, Enfield, EN1 1TW', slug: 'enfield' },
  { id: 5, site: 'Leeds', address: 'Enterprise Rent a Car, 75, Low Road, Leeds, LS10 1RH', slug: 'leeds' },
  { id: 6, site: 'Sheffield', address: 'Morrisons, 1 Oxclose Park Rd N, Halfway, Sheffield S20 8GN', slug: 'sheffield' }
];

// New donation tiers
const DONATION_TIERS = [
  { value: 0, price: 0, entries: 0, label: 'No donation' },
  { value: 5, price: 5.00, entries: 5, label: '£5.00 donation - 5 entries' },
  { value: 10, price: 10.00, entries: 10, label: '£10.00 donation - 10 entries' },
  { value: 15, price: 15.00, entries: 20, label: '£15.00 donation - 20 entries' },
  { value: 20, price: 20.00, entries: 30, label: '£20.00 donation - 30 entries' }
];

const RafflePage = () => {
  const urlLocation = useLocation();
  
  const [formData, setFormData] = useState({
    location: '', // New field for selected location
    carReg: '',
    fullName: '',
    email: '',
    phone: '',
    donationTier: 0
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);

  const FREE_TICKET_LIMIT = 1;

  // Load Stripe.js securely on component mount - Safari compatible
  useEffect(() => {
    const loadStripe = async () => {
      try {
        if (window.Stripe) {
          setStripeLoaded(true);
          return;
        }

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        
        if (isSafari) {
          setTimeout(() => {
            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3/';
            script.async = false;
            script.defer = true;
            script.crossOrigin = 'anonymous';
            
            script.onload = () => {
              setTimeout(() => {
                if (window.Stripe) {
                  setStripeLoaded(true);
                  console.log('Stripe.js loaded successfully in Safari');
                } else {
                  setErrors(prev => ({ ...prev, stripe: 'Payment system loading issue. Please refresh and try again.' }));
                }
              }, 500);
            };
            
            script.onerror = () => {
              console.error('Failed to load Stripe.js in Safari');
              setErrors(prev => ({ ...prev, stripe: 'Payment system blocked. Please enable JavaScript and try again.' }));
            };

            document.head.appendChild(script);
          }, 100);
        } else {
          const script = document.createElement('script');
          script.src = 'https://js.stripe.com/v3/';
          script.async = true;
          
          script.onload = () => {
            setStripeLoaded(true);
            console.log('Stripe.js loaded successfully');
          };
          
          script.onerror = () => {
            console.error('Failed to load Stripe.js');
            setErrors(prev => ({ ...prev, stripe: 'Payment system unavailable. Please try again later.' }));
          };

          document.head.appendChild(script);
        }
      } catch (error) {
        console.error('Error loading Stripe:', error);
        setErrors(prev => ({ ...prev, stripe: 'Payment system error.' }));
      }
    };

    loadStripe();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(urlLocation.search);
    if (urlParams.get('success') === 'true' && !submitted) {
      setSubmitted(true);
      const storedData = sessionStorage.getItem('raffleFormData');
      if (storedData) {
        setFormData(JSON.parse(storedData));
        sessionStorage.removeItem('raffleFormData');
      }
    } else if (urlParams.get('canceled') === 'true') {
      setErrors({ submit: 'Payment was canceled. You can try again.' });
    }
  }, [urlLocation, submitted]);

  const validateForm = () => {
    const newErrors = {};
    
    // Validate location selection
    if (!formData.location) {
      newErrors.location = 'Please select a location';
    }
    
    if (!formData.carReg.trim()) newErrors.carReg = 'Car registration is required';
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    const phoneRegex = /^(\+44|0)[1-9]\d{8,9}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid UK phone number';
    }
    
    console.log('Form validation data:', formData);
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Helper functions for the new pricing structure
  const getSelectedTier = () => DONATION_TIERS.find(tier => tier.value === formData.donationTier) || DONATION_TIERS[0];
  const getDonationAmount = () => getSelectedTier().price;
  const getPaidEntries = () => getSelectedTier().entries;
  const getTotalEntries = () => FREE_TICKET_LIMIT + getPaidEntries();
  
  // Get selected location data
  const getSelectedLocation = () => locations.find(loc => loc.site === formData.location);

  const getApiUrl = (endpoint) => {
    if (typeof window !== 'undefined') {
      const protocol = 'https:';
      const host = window.location.host;
      return `${protocol}//${host}/api${endpoint}`;
    }
    return `/api${endpoint}`;
  };

  const submitFreeEntryToBackend = async () => {
    const entryData = {
      location: formData.location,
      carRegistration: formData.carReg.toUpperCase(),
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      totalTickets: getTotalEntries(),
      paidTickets: getPaidEntries(),
      amountPaid: getDonationAmount(),
    };

    try {
      const response = await fetch(getApiUrl('/submit-entry'), {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(entryData),
        mode: 'cors',
        credentials: 'same-origin'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit entry.');
      }
      
      console.log('Free entry submitted successfully');
    } catch (error) {
      console.error('Failed to save free entry:', error);
      throw error;
    }
  };

  const processPaymentWithStripe = async () => {
    const totalAmountInPence = Math.round(getDonationAmount() * 100);
    const paidEntries = getPaidEntries();

    console.log('Starting payment process with data:', {
      location: formData.location,
      carReg: formData.carReg,
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      donationAmount: getDonationAmount(),
      totalAmount: totalAmountInPence,
      paidEntries: paidEntries,
      donationTier: formData.donationTier
    });

    if (!stripeLoaded || !window.Stripe) {
      throw new Error('Payment system is still loading. Please wait a moment and try again.');
    }

    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!stripePublishableKey) {
      console.error('Stripe publishable key missing');
      throw new Error('Payment configuration is missing. Please contact support.');
    }

    if (!formData.carReg || !formData.fullName || !formData.email || !formData.location) {
      throw new Error('Missing required information. Please fill in all fields.');
    }

    sessionStorage.setItem('raffleFormData', JSON.stringify(formData));

    try {
      const requestData = {
        amount: totalAmountInPence,
        currency: 'gbp',
        entries: paidEntries,
        customerEmail: formData.email,
        success_url: 'https://www.driven-auto.co.uk/raffle/success',
        cancel_url: 'https://www.driven-auto.co.uk/raffle',
        metadata: {
          location: formData.location,
          phone: formData.phone,
          fullName: formData.fullName,
          carReg: formData.carReg.toUpperCase(),
          totalTickets: getTotalEntries().toString(),
          paidTickets: getPaidEntries().toString(),
          donationTier: formData.donationTier.toString()
        },
      };

      console.log('Sending request to create checkout session:', requestData);

      const response = await fetch(getApiUrl('/create-checkout-session'), {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(requestData),
        mode: 'cors',
        credentials: 'same-origin',
        referrerPolicy: 'strict-origin-when-cross-origin'
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response error:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText || 'Unknown server error' };
        }
        
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Checkout session response:', responseData);

      if (!responseData.sessionId) {
        throw new Error('Invalid response from payment service');
      }

      const { sessionId } = responseData;
      
      try {
        if (typeof window.Stripe !== 'function') {
          throw new Error('Payment system not properly initialized');
        }

        const stripe = window.Stripe(stripePublishableKey);
        if (!stripe) {
          throw new Error('Failed to initialize payment system');
        }

        console.log('Redirecting to Stripe checkout with session:', sessionId);

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          console.error('Stripe redirect error:', error);
          if (error.type === 'network_error') {
            throw new Error('Network connection issue. Please check your internet and try again.');
          }
          throw new Error(error.message);
        }
      } catch (stripeError) {
        console.error('Stripe initialization error:', stripeError);
        throw new Error('Payment system error: ' + stripeError.message);
      }

    } catch (error) {
      console.error('Payment processing error:', error);
      if (error.message.includes('network') || error.message.includes('fetch')) {
        throw new Error('Connection issue detected. Please check your internet connection and try again.');
      }
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to payment service. Please check your connection and try again.');
      }
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (formData.donationTier > 0 && !stripeLoaded) {
      setErrors({ submit: 'Payment system is still loading. Please wait a moment and try again.' });
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      if (formData.donationTier > 0) {
        setPaymentProcessing(true);
        await processPaymentWithStripe();
      } else {
        await submitFreeEntryToBackend();
        setSubmitted(true);
      }
    } catch (error) {
      setErrors({ submit: error.message || 'An unexpected error occurred.' });
    } finally {
      setSubmitting(false);
      setPaymentProcessing(false);
    }
  };

  if (submitted) {
    const selectedLocation = getSelectedLocation();
    return (
      <div className="raffle-container">
        <div className="raffle-success">
          <CheckCircle size={64} className="success-icon" />
          <h1>Entry Submitted Successfully!</h1>
          <div className="success-details">
            <h2>Your Raffle Details</h2>
            <div className="detail-row"><strong>Location:</strong> {formData.location}</div>
            {selectedLocation && (
              <div className="detail-row"><strong>Address:</strong> {selectedLocation.address}</div>
            )}
            <div className="detail-row"><strong>Car Registration:</strong> {formData.carReg.toUpperCase()}</div>
            <div className="detail-row"><strong>Total Entries:</strong> {getTotalEntries()}</div>
            <div className="detail-row"><strong>Free Entries:</strong> {FREE_TICKET_LIMIT}</div>
            <div className="detail-row"><strong>Paid Entries:</strong> {getPaidEntries()}</div>
            <div className="detail-row"><strong>Donation Amount:</strong> £{getDonationAmount().toFixed(2)}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="raffle-container">
      <div className="raffle-content">
        <form onSubmit={handleSubmit} className="raffle-form">
          <div className="form-group">
            <label htmlFor="location"><MapPin size={18} /> Select Location</label>
            <select 
              id="location" 
              value={formData.location} 
              onChange={(e) => handleInputChange('location', e.target.value)}
              className={errors.location ? 'form-input-error' : ''}
            >
              <option value="">-- Select a location --</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.site}>
                  {loc.site} - {loc.address}
                </option>
              ))}
            </select>
            {errors.location && <span className="form-error">{errors.location}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="carReg"><Car size={18} /> Car Registration</label>
            <input 
              type="text" 
              id="carReg" 
              value={formData.carReg} 
              onChange={(e) => handleInputChange('carReg', e.target.value)} 
              className={errors.carReg ? 'form-input-error' : ''} 
              placeholder="e.g. AB12 CDE"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.carReg && <span className="form-error">{errors.carReg}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="fullName"><User size={18} /> Full Name</label>
            <input 
              type="text" 
              id="fullName" 
              value={formData.fullName} 
              onChange={(e) => handleInputChange('fullName', e.target.value)} 
              className={errors.fullName ? 'form-input-error' : ''} 
            />
            {errors.fullName && <span className="form-error">{errors.fullName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email"><Mail size={18} /> Email Address</label>
            <input 
              type="email" 
              id="email" 
              value={formData.email} 
              onChange={(e) => handleInputChange('email', e.target.value)} 
              className={errors.email ? 'form-input-error' : ''} 
            />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone"><Phone size={18} /> Phone Number</label>
            <input 
              type="tel" 
              id="phone" 
              value={formData.phone} 
              onChange={(e) => handleInputChange('phone', e.target.value)} 
              className={errors.phone ? 'form-input-error' : ''} 
            />
            {errors.phone && <span className="form-error">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="donationTier"><CreditCard size={18} /> Donation Amount (Optional)</label>
            <select 
              id="donationTier" 
              value={formData.donationTier} 
              onChange={(e) => handleInputChange('donationTier', parseInt(e.target.value))}
            >
              {DONATION_TIERS.map((tier) => (
                <option key={tier.value} value={tier.value}>
                  {tier.label}
                </option>
              ))}
            </select>
          </div>

          <div className="raffle-summary">
            <div className="summary-row"><span>Free entries:</span> <span>{FREE_TICKET_LIMIT}</span></div>
            <div className="summary-row"><span>Paid entries:</span> <span>{getPaidEntries()}</span></div>
            <div className="summary-row"><span>Total entries:</span> <span>{getTotalEntries()}</span></div>
            <div className="summary-row total"><span>Donation amount:</span> <span>£{getDonationAmount().toFixed(2)}</span></div>
          </div>

          <div className="form-actions">
            {errors.submit && <div className="form-error-general"><AlertCircle size={18} /> {errors.submit}</div>}
            {errors.stripe && <div className="form-error-general"><AlertCircle size={18} /> {errors.stripe}</div>}
            <button 
              type="submit" 
              disabled={submitting || paymentProcessing || (formData.donationTier > 0 && !stripeLoaded)} 
              className="raffle-button-primary"
            >
              {submitting ? (paymentProcessing ? 'Redirecting to Payment...' : 'Submitting...') : (getDonationAmount() > 0 ? `Donate £${getDonationAmount().toFixed(2)} & Submit` : 'Submit Free Entry')}
            </button>
            {formData.donationTier > 0 && !stripeLoaded && (
              <div className="loading-message">Loading payment system...</div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RafflePage;